/**
 * Vérifier la fiche d'un enfant — le même parcours que pour l'ajouter.
 *
 * ## Pourquoi pas l'accordéon
 *
 * La fiche enfant est une pile de sections repliées où l'on modifie et
 * enregistre un bloc à la fois. Pour consulter une information, c'est ce qu'il
 * faut. Pour vérifier, c'est l'inverse de ce qu'il faut : le parent voyait dans
 * quelle section il manquait quelque chose, jamais dans quel champ, et devait
 * remonter chercher un bouton « Enregistrer » à chaque bloc.
 *
 * Ici il reparcourt la fiche entière, partie par partie, comme à la création.
 * Ce qui manque est en orange sous ses yeux, dans le champ même, et part dès
 * que le champ est rempli (`manques.ts`). Une seule validation à la fin, et
 * l'enfant suivant est proposé juste après.
 *
 * ## Orange dès l'ouverture, et ce n'est pas une accusation
 *
 * Les manques sont affichés avant toute action du parent : 2 939 fiches sur
 * 2 943 datent d'avant ces questions, personne ne s'est trompé. `TonDesManques`
 * met donc tous les messages en orange plutôt qu'en rouge — le rouge dirait
 * « vous avez fait une erreur », l'orange dit « il reste ça ».
 *
 * ## Pourquoi tout est réécrit, et pas seulement ce qui change
 *
 * `versLignes()` produit les sept lignes de la fiche d'un bloc. Écrire
 * uniquement les champs modifiés demanderait de comparer avant/après sur une
 * quarantaine de champs répartis sur sept tables, pour économiser des écritures
 * qui ne coûtent rien. Un `upsert` par table, la même fonction qu'à la
 * création : une seule façon d'écrire une fiche, donc une seule à vérifier.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useKidStore } from '../../stores/kidStore';
import { useSchoolStore } from '../../stores/schoolStore';
import { useAuthorizedPersonsStore } from '../../stores/authorizedPersonsStore';
import { VERSION_POLITIQUE } from '../../lib/confidentialite';
import { completudeEnfant } from '../../lib/completude';
import { anneeScolaireCourante, resumeClasse, type ClassesConnues } from '../../lib/classes/regles';
import {
  classeDeLEnfant,
  enregistrerClasse,
  lireClassesConnues,
  signalerClasseManquante,
} from '../../lib/classes/gateway.supabase';
import LigneClasse from '../../components/forms/kid/LigneClasse';

import Etapes from '../../components/forms/kid/Etapes';
import EtapeIdentite from '../../components/forms/kid/EtapeIdentite';
import EtapeSante from '../../components/forms/kid/EtapeSante';
import EtapeVie from '../../components/forms/kid/EtapeVie';
import EtapeSorties from '../../components/forms/kid/EtapeSorties';
import EtapeAutorisations from '../../components/forms/kid/EtapeAutorisations';
import Recapitulatif from '../../components/forms/kid/Recapitulatif';
import BandeauDesManques from '../../components/forms/kid/BandeauDesManques';
import { MentionObligatoire, TonDesManques } from '../../components/forms/kid/champs';
import {
  champsEnDefaut,
  manquesSignales,
  nombresParEtape,
  reunir,
  type Signales,
} from '../../components/forms/kid/manques';
import {
  ETAPES,
  depuisLignes,
  premiereEtapeIncomplete,
  type ContexteFiche,
  ecoleEnregistree,
  versLignes,
  type Erreurs,
  type Etape,
  type Fiche,
  type LignesLues,
} from '../../components/forms/kid/fiche';

/** Les lignes de la base, telles que le magasin les rapporte. */
const lignesDe = (k: NonNullable<ReturnType<typeof useKidStore.getState>['currentKid']>): LignesLues => ({
  kids: k,
  health: k.health,
  allergies: k.allergies,
  activities: k.activities,
  departure: k.departure,
  inclusion: k.inclusion,
  image_rights: k.image_rights,
});

const Verification = ({ id }: { id: string }) => {
  const { user, profile } = useAuthStore();
  const { kids, fetchKids, fetchKid, updateKid } = useKidStore();
  const { schools, fetchSchools } = useSchoolStore();
  const { persons, fetchPersons } = useAuthorizedPersonsStore();

  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [depart, setDepart] = useState<Fiche | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  /*
    La photo deja en banque, prise sur la fiche chargee — et non via la liste
    des enfants, qui arrive plus tard. Sans elle, la verification ouvrait sur
    une pastille grise et « Ajouter une photo » : le parent croyait la sienne
    perdue et la televersait a nouveau pour rien.
  */
  const [photoExistante, setPhotoExistante] = useState<string | null>(null);
  const [etape, setEtape] = useState<Etape>('identite');
  /* Les champs en défaut au dernier enregistrement refusé, `null` tant qu'aucun ne l'a été. */
  const [refus, setRefus] = useState<Signales | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const [termine, setTermine] = useState(false);
  /* Les centres reliés aux écoles et leurs classes ; `undefined` tant qu'ils ne sont pas lus. */
  const [classesConnues, setClassesConnues] = useState<ClassesConnues | undefined>(undefined);
  const annee = useMemo(() => anneeScolaireCourante(), []);

  /* Le garde-fou du double envoi : une reference, pas un etat. */
  const enCours = useRef(false);

  useEffect(() => {
    fetchKids();
    fetchSchools();
    fetchPersons();
  }, [fetchKids, fetchSchools, fetchPersons]);

  useEffect(() => {
    let annule = false;
    /*
      La fiche, et sa classe de l'année (`kid_classes`), lue à part : elle ne
      fait pas partie des lignes de la fiche. Les classes des centres suivent ;
      sans elles, la classe n'est ni demandée ni réécrite.
    */
    Promise.all([fetchKid(id), classeDeLEnfant(id, annee).catch(() => null)]).then(([k, classe]) => {
      if (annule || !k) return;
      const lue = depuisLignes(lignesDe(k));
      lue.identite.classeId = classe?.id ?? '';
      setFiche(lue);
      setDepart(lue);
      setPhotoExistante(k.photo_signed_url ?? null);
    });
    lireClassesConnues()
      .then((c) => {
        if (!annule) setClassesConnues(c);
      })
      .catch((e) => console.error('Classes des centres illisibles :', e));
    return () => {
      annule = true;
    };
  }, [id, fetchKid, annee]);

  const enfant = kids.find((k) => k.id === id);

  /*
    Venu de l'étape 2 des journées pédagogiques (`?retour=journees&ecole=…`) :
    la fin ramène à cette étape, l'enfant coché, et non à la page des stages.
  */
  const [parametres] = useSearchParams();
  const ecoleDesJournees = parametres.get('retour') === 'journees' ? parametres.get('ecole') : null;
  const retourJournees = ecoleDesJournees
    ? `/journees-pedagogiques?${new URLSearchParams({ ecole: ecoleDesJournees, enfant: id })}`
    : null;

  /* Les numéros de registre national du parent et des autres enfants : pas ceux de cette fiche. */
  const contexte = useMemo<ContexteFiche>(
    () => ({
      nrnInterdits: [profile?.nnational ?? '', ...kids.filter((k) => k.id !== id).map((k) => k.n_national ?? '')].filter(Boolean),
      classes: classesConnues,
    }),
    [profile, kids, id, classesConnues],
  );

  /*
    Les champs signalés : ceux qui manquaient à l'ouverture, et ceux d'un
    enregistrement refusé. Leurs messages suivent la fiche telle qu'elle est :
    ils partent dès que le champ est en règle. Avant le 24/10/2026, ils
    étaient figés sur la fiche d'ouverture et restaient sous un champ rempli.
    Un champ qui n'a jamais été signalé ne s'affiche pas en cours de frappe.
  */
  const signalesALOuverture = useMemo<Signales>(
    () => (depart ? champsEnDefaut(depart, contexte) : {}),
    [depart, contexte],
  );
  const manques = useMemo(
    () => (fiche ? manquesSignales(fiche, contexte, reunir(signalesALOuverture, refus ?? {})) : {}),
    [fiche, contexte, signalesALOuverture, refus],
  );

  /* Ce qui reste a faire maintenant, pour la barre de progression et la fin. */
  const restants = fiche ? premiereEtapeIncomplete(fiche, contexte) : null;

  /* Toutes les etapes sont accessibles : on VERIFIE, on ne saisit pas a neuf. */
  const toutes = ETAPES.map((e) => e.cle);

  const allerA = (cible: Etape) => {
    setEchec(null);
    setEtape(cible);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /*
    On avance TOUJOURS, meme si l'etape est incomplete.

    A la creation, « Continuer » refuse d'avancer tant que l'etape n'est pas
    remplie, et c'est juste : la fiche n'existe pas encore. Ici elle existe, et
    le parent vient la relire en entier. Le bloquer a la premiere lacune —
    typiquement le nom du medecin, absent chez 1 128 enfants — l'empecherait de
    voir les quatre parties suivantes, et donc de savoir ce qui l'attend s'il
    doit aller chercher l'information.

    Ce qui manque reste orange, le recapitulatif le redit, et c'est la
    validation finale qui refuse — en le ramenant a la bonne etape.
  */
  const continuer = () => {
    const i = ETAPES.findIndex((e) => e.cle === etape);
    allerA(ETAPES[Math.min(i + 1, ETAPES.length - 1)].cle);
  };

  const revenir = () => {
    const i = ETAPES.findIndex((e) => e.cle === etape);
    allerA(ETAPES[Math.max(i - 1, 0)].cle);
  };

  /** Les messages à afficher sur une étape : ce qui, parmi les champs signalés, manque encore. */
  const messages = (cle: Etape): Erreurs => manques[cle] ?? {};

  const enregistrer = async () => {
    if (!fiche || !user || enCours.current) return;

    /*
      Une fiche incomplète : le parent est ramené à la première partie en
      cause, où le bandeau dit ce qui manque et mène ensuite au plus court.
      Plus de bandeau rouge général : il restait sur toutes les étapes.
    */
    const incomplete = premiereEtapeIncomplete(fiche, contexte);
    if (incomplete) {
      setRefus(champsEnDefaut(fiche, contexte));
      allerA(incomplete);
      return;
    }

    enCours.current = true;
    setEnregistrement(true);
    setEchec(null);

    try {
      const t = versLignes(fiche, id, user.id, VERSION_POLITIQUE);

      /*
        `versLignes` date la fiche : `last_verified_at` y vaut maintenant. C'est
        cette ecriture qui la fait passer d'« a verifier » a « a jour ».
      */
      await updateKid(id, t.kids);

      /*
        La classe de l'année : écrite, ou retirée quand l'école n'en a plus
        (changement d'école, école tapée à la main, classe signalée hors
        liste). Seulement si les classes ont pu être lues : sans elles, le
        formulaire n'a rien demandé, et il ne doit rien effacer.
      */
      if (classesConnues) {
        const ecole = ecoleEnregistree(fiche.identite);
        await enregistrerClasse(id, ecole, ecole, annee, fiche.identite.classeId);
      }

      const ecritures: {
        quoi: string;
        ecrire: () => PromiseLike<{ error: { message: string } | null }>;
      }[] = [
        { quoi: 'la fiche santé', ecrire: () => supabase.from('kid_health').upsert(t.kid_health, { onConflict: 'kid_id' }) },
        { quoi: 'les allergies', ecrire: () => supabase.from('kid_allergies').upsert(t.kid_allergies, { onConflict: 'kid_id' }) },
        { quoi: 'les activités', ecrire: () => supabase.from('kid_activities').upsert(t.kid_activities, { onConflict: 'kid_id' }) },
        { quoi: 'les sorties', ecrire: () => supabase.from('kid_departure').upsert(t.kid_departure, { onConflict: 'kid_id' }) },
        { quoi: 'les besoins particuliers', ecrire: () => supabase.from('kid_inclusion').upsert(t.kid_inclusion, { onConflict: 'kid_id' }) },
        { quoi: 'les autorisations d’image', ecrire: () => supabase.from('kid_image_rights').upsert(t.kid_image_rights, { onConflict: 'kid_id' }) },
      ];

      for (const { quoi, ecrire } of ecritures) {
        const { error } = await ecrire();
        if (error) throw new Error(`${quoi} n'ont pas pu être enregistrées (${error.message})`);
      }

      /*
        La photo en dernier, et son echec n'annule rien : une fiche verifiee
        sans nouveau portrait vaut mieux qu'une verification perdue parce qu'un
        JPEG a mal voyage.
      */
      if (photo) {
        const extension = photo.type === 'image/png' ? 'png' : 'jpg';
        const chemin = `${id}.${extension}`;
        const { error: erreurTeleversement } = await supabase.storage
          .from('kid-photos')
          .upload(chemin, photo, { upsert: true, contentType: photo.type });
        if (!erreurTeleversement) {
          await supabase.from('kids').update({ photo_url: chemin }).eq('id', id);
        }
      }

      await fetchKids();
      setTermine(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      setEchec(
        err instanceof Error
          ? `L'enregistrement a échoué : ${err.message}`
          : "L'enregistrement a échoué. Vos réponses sont conservées, réessayez dans un instant.",
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      enCours.current = false;
      setEnregistrement(false);
    }
  };

  /* --- L'enfant suivant -------------------------------------------------- */

  /*
    Le suivant est le premier AUTRE enfant sur lequel il reste quelque chose a
    faire. Sans cette suite, une famille de trois enfants devrait retourner a
    l'accueil deux fois pour comprendre qu'il en reste deux.
  */
  const suivant = kids.find(
    (k) => k.id !== id && completudeEnfant(lignesDe(k)).aFaire,
  );

  if (!fiche || !depart) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden="true" />
        <span className="sr-only">Chargement de la fiche…</span>
      </div>
    );
  }

  const prenom = fiche.identite.prenom.trim() || enfant?.prenom || 'votre enfant';

  if (termine) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl bg-green-50 p-6 text-center ring-1 ring-green-200">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white">
            <CheckCircle2 className="h-7 w-7 text-green-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-gray-900">
            La fiche de {prenom} est à jour
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-gray-700">
            Nous vous la redemanderons dans six semaines, pas avant.
          </p>
        </div>

        <div className="mt-6 space-y-3">
          {retourJournees && (
            <Link to={retourJournees} className="btn-primary flex min-h-[52px] w-full items-center justify-center text-base">
              Revenir aux journées pédagogiques
            </Link>
          )}
          {suivant && (
            <Link
              to={`/kids/${suivant.id}/verifier${ecoleDesJournees ? `?${new URLSearchParams({ retour: 'journees', ecole: ecoleDesJournees })}` : ''}`}
              className={
                retourJournees
                  ? 'btn-outline flex min-h-[52px] w-full items-center justify-center'
                  : 'btn-primary flex min-h-[52px] w-full items-center justify-center text-base'
              }
            >
              Vérifier la fiche de {suivant.prenom}
            </Link>
          )}
          {!retourJournees && (
            <Link
              /* L'enfant qu'on vient de vérifier est choisi d'office sur la page des stages. */
              to={`/activities?enfant=${id}`}
              className={
                suivant
                  ? 'btn-outline flex min-h-[52px] w-full items-center justify-center'
                  : 'btn-primary flex min-h-[52px] w-full items-center justify-center text-base'
              }
            >
              Inscrire {enfant?.prenom ?? 'cet enfant'} à un stage
            </Link>
          )}
          <Link
            to="/dashboard"
            className="flex min-h-[44px] w-full items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Retour à l’accueil
          </Link>
        </div>
      </div>
    );
  }

  const titre = ETAPES.find((e) => e.cle === etape)?.titre ?? '';
  const nombreManques = Object.values(signalesALOuverture).reduce(
    (total, champs) => total + (champs?.length ?? 0),
    0,
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:py-10">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex min-h-[44px] items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="mr-2 h-5 w-5" aria-hidden="true" />
        Retour à l’accueil
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
          Vérifier la fiche de {prenom}
        </h1>
        <p className="mt-1 text-gray-600">
          {nombreManques > 0
            ? `Parcourez les cinq parties : ce qui manque est signalé en orange. Comptez environ une minute.`
            : 'Parcourez les cinq parties et confirmez à la fin. Comptez environ une minute.'}
        </p>
      </div>

      <Etapes courante={etape} atteintes={toutes} onAller={allerA} aCompleter={nombresParEtape(manques)} />

      {echec && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm text-red-800">{echec}</p>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-8">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="hidden text-xl font-bold text-gray-900 sm:block">{titre}</h2>
          {etape !== 'recapitulatif' && <MentionObligatoire />}
        </div>

        {/*
          Tout le formulaire est en ton « manque » : les messages presents des
          l'ouverture ne signalent pas une faute du parent, ils signalent une
          question qui ne lui avait jamais ete posee.
        */}
        {etape !== 'recapitulatif' && refus?.[etape] && (
          <BandeauDesManques manques={messages(etape)} prochaine={restants} onAller={allerA} />
        )}

        <TonDesManques>
          {etape === 'identite' && (
            <EtapeIdentite
              valeur={fiche.identite}
              onChange={(identite) => setFiche((f) => f && { ...f, identite })}
              erreurs={messages('identite')}
              onSuivant={continuer}
              photo={photo}
              onPhoto={setPhoto}
              photoExistante={photoExistante}
              /* Pas de copie depuis la fratrie : on verifie une fiche qui existe. */
              fratrie={[]}
              onCopierFratrie={() => {}}
              classes={classesConnues}
              annee={annee}
              onSignalerClasse={(classe) =>
                signalerClasseManquante({
                  ecoleId: fiche.identite.ecole,
                  classe,
                  enfant: { prenom: fiche.identite.prenom.trim(), nom: fiche.identite.nom.trim() },
                })
              }
            />
          )}

          {etape === 'sante' && (
            <EtapeSante
              valeur={fiche.sante}
              onChange={(sante) => setFiche((f) => f && { ...f, sante })}
              erreurs={messages('sante')}
              onPrecedent={revenir}
              onSuivant={continuer}
              prenom={fiche.identite.prenom}
            />
          )}

          {etape === 'vie' && (
            <EtapeVie
              valeur={fiche.vie}
              onChange={(vie) => setFiche((f) => f && { ...f, vie })}
              erreurs={messages('vie')}
              onPrecedent={revenir}
              onSuivant={continuer}
              prenom={fiche.identite.prenom}
            />
          )}

          {etape === 'sorties' && (
            <EtapeSorties
              valeur={fiche.sorties}
              onChange={(sorties) => setFiche((f) => f && { ...f, sorties })}
              erreurs={messages('sorties')}
              onPrecedent={revenir}
              onSuivant={continuer}
              prenom={fiche.identite.prenom}
            />
          )}

          {etape === 'autorisations' && (
            <EtapeAutorisations
              valeur={fiche.autorisations}
              onChange={(autorisations) => setFiche((f) => f && { ...f, autorisations })}
              erreurs={messages('autorisations')}
              onPrecedent={revenir}
              onSuivant={continuer}
              prenom={fiche.identite.prenom}
            />
          )}
        </TonDesManques>

        {etape === 'recapitulatif' && restants && (
          <div className="mb-6 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              Il manque encore quelque chose
            </h3>
            <p className="mt-1 text-sm text-amber-900/80">
              Nous ne pouvons pas enregistrer tant que{' '}
              <button
                type="button"
                onClick={() => allerA(restants)}
                className="font-medium underline underline-offset-2"
              >
                {ETAPES.find((e) => e.cle === restants)?.titre.toLowerCase()}
              </button>{' '}
              n’est pas complet.
            </p>
          </div>
        )}

        {etape === 'recapitulatif' && (
          <Recapitulatif
            fiche={fiche}
            photo={photo}
            photoExistante={photoExistante}
            ecoles={schools}
            personnes={persons}
            onModifier={allerA}
            onPrecedent={revenir}
            onEnregistrer={enregistrer}
            enCours={enregistrement}
            libelleEnregistrer="Tout est exact, enregistrer"
            classe={(() => {
              const r = resumeClasse(fiche.identite.ecole, fiche.identite.classeId, classesConnues, annee);
              return r ? <LigneClasse resume={r} /> : undefined;
            })()}
          />
        )}
      </div>

      <p className="mt-6 text-center text-sm text-gray-500">
        <Link to={`/kids/${id}`} className="underline underline-offset-2 hover:text-gray-700">
          Voir la fiche complète sans la vérifier
        </Link>
      </p>
    </div>
  );
};

/**
 * L'enveloppe qui remet tout à zéro d'un enfant à l'autre.
 *
 * Sans elle, « Vérifier la fiche de Noah » affichait immédiatement « La fiche
 * de Noah est à jour », alors qu'il restait des champs à remplir : React Router
 * garde le même composant monté quand seul le `:id` de l'URL change, donc
 * `termine` — passé à vrai en validant Léa — survivait au changement d'enfant.
 * Le parent devait repasser par l'accueil pour que la page reparte de zéro.
 *
 * `key={id}` démonte et remonte : tous les états repartent de leur valeur
 * initiale, sans qu'aucun ait à être remis à zéro un par un. Les réinitialiser
 * à la main marcherait aussi, jusqu'au jour où l'on ajoute un état et où l'on
 * oublie de l'ajouter à la liste.
 */
const VerifierKidPage = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return <Verification key={id} id={id} />;
};

export default VerifierKidPage;
